'use client';

import { useState } from 'react';
import { PartyPopper, ChevronRight } from 'lucide-react';

interface TagsCompleteModalProps {
  onFinish: () => void;
}

/**
 * Completion modal shown at the end of the Tags Tour.
 * Two-screen carousel celebrating completion and encouraging tag usage.
 */
export function TagsCompleteModal({ onFinish }: TagsCompleteModalProps) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const isLastScreen = currentScreen === 1;

  const handleNext = () => {
    if (isLastScreen) {
      onFinish();
    } else {
      setCurrentScreen(1);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Tags tour complete"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Icon */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-blue/[0.08]">
          <PartyPopper className="h-8 w-8 text-accent-blue" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">Tags Tour Complete!</h2>

        {/* Content */}
        {currentScreen === 0 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              You now know how to create tags, assign them to conversations, and use filters to quickly find what you
              need. Tags give your team a shared vocabulary for organizing every client interaction.
            </p>
          </div>
        )}

        {currentScreen === 1 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              As your agency grows, tags become even more powerful. Create tags for different lines of business, campaign
              types, or urgency levels to keep your entire team aligned and efficient.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-center">
          <button
            onClick={handleNext}
            aria-label={isLastScreen ? 'Finish tour' : 'Next screen'}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-base font-semibold text-white bg-accent-blue hover:brightness-110 transition-all shadow-md"
          >
            {isLastScreen ? (
              'Finish Tour'
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 mt-5">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
